import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Lock } from 'react-feather';
import { Button, Form, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { createCustomRoleAPI, getRoleMatrixAPI, updateCustomRoleAPI } from '../../../Services/mockApi';
import PageHeader from '../../../Components/shared/PageHeader';
import LoadingSkeleton from '../../../Components/shared/LoadingSkeleton';
import EmptyState from '../../../Components/shared/EmptyState';
import DrawerPanel from '../../../Components/shared/DrawerPanel';
import { companyUrl } from '../../../utils/mhNav';
import styles from './RoleMatrix.module.scss';

const PERM_KEYS = ['view', 'create', 'edit', 'approve'];

function PermCell({ locked, perms, roleId, moduleId, onToggle }) {
  if (locked) {
    return (
      <div className={styles.permGrid}>
        {PERM_KEYS.map((k) => (
          <span key={k} className="text-center">
            {perms?.[k] ? '✓' : '—'}
          </span>
        ))}
      </div>
    );
  }
  return (
    <div className={styles.permGrid}>
      {PERM_KEYS.map((k) => (
        <Form.Check
          key={k}
          type="checkbox"
          checked={!!perms?.[k]}
          onChange={(e) => onToggle(moduleId, roleId, k, e.target.checked)}
          label={k.slice(0, 1).toUpperCase()}
          className="m-0"
        />
      ))}
    </div>
  );
}

export default function RoleMatrix() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [newRole, setNewRole] = useState({ name: '', description: '', baseRoleId: 'viewer' });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getRoleMatrixAPI();
      setData(res.data);
      setHasLoaded(true);
      setSelectedRoleId((cur) => cur || res.data?.roles?.[0]?.id || null);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const selectedRole = useMemo(() => data?.roles?.find((r) => r.id === selectedRoleId), [data, selectedRoleId]);

  const onToggle = async (moduleId, roleId, k, value) => {
    try {
      await updateCustomRoleAPI(roleId, moduleId, k, value);
      toast.success('Permission updated');
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const createRole = async () => {
    try {
      await createCustomRoleAPI({
        name: newRole.name,
        description: newRole.description,
        baseRoleId: newRole.baseRoleId,
      });
      toast.success('Custom role created');
      setDrawerOpen(false);
      setNewRole({ name: '', description: '', baseRoleId: 'viewer' });
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (loading && !hasLoaded) {
    return (
      <div>
        <PageHeader title="Role matrix" breadcrumbs={[{ label: 'Company', path: companyUrl('dashboard') }, { label: 'Users', path: companyUrl('users') }, { label: 'Roles' }]} />
        <LoadingSkeleton rows={5} cols={4} />
      </div>
    );
  }

  if (error && !hasLoaded) {
    return (
      <div>
        <PageHeader title="Role matrix" breadcrumbs={[{ label: 'Company', path: companyUrl('dashboard') }, { label: 'Users', path: companyUrl('users') }, { label: 'Roles' }]} />
        <EmptyState icon={AlertTriangle} title="Could not load" description={error.message} actionLabel="Retry" onAction={load} />
      </div>
    );
  }

  const { modules = [], roles = [], permissions = {} } = data || {};

  return (
    <div>
      <PageHeader
        title="Role matrix"
        breadcrumbs={[{ label: 'Company', path: companyUrl('dashboard') }, { label: 'Users', path: companyUrl('users') }, { label: 'Roles' }]}
        primaryAction={{ label: 'Create custom role', onClick: () => setDrawerOpen(true) }}
      />

      {!modules.length ? (
        <EmptyState icon={AlertTriangle} title="Empty matrix" description="No modules defined." actionLabel="Retry" onAction={load} />
      ) : (
        <div className="d-flex flex-column flex-lg-row gap-3">
          <div className="flex-grow-1">
            <div className={styles.matrixWrap}>
              <Table bordered size="sm" className="mb-0 text-center align-middle">
                <thead>
                  <tr>
                    <th className="text-start">Module</th>
                    {roles.map((r) => (
                      <th
                        key={r.id}
                        className={`${styles.roleHead} ${selectedRoleId === r.id ? styles.roleHeadActive : ''}`}
                        onClick={() => setSelectedRoleId(r.id)}
                      >
                        {r.locked && <Lock size={12} className="me-1" />}
                        {r.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {modules.map((m) => (
                    <tr key={m.id}>
                      <td className="text-start fw-medium">{m.label}</td>
                      {roles.map((r) => {
                        const key = `${m.id}:${r.id}`;
                        const p = permissions[key] || {};
                        return (
                          <td key={r.id}>
                            <PermCell locked={r.locked} perms={p} roleId={r.id} moduleId={m.id} onToggle={onToggle} />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
          <div className={styles.sidePanel} style={{ minWidth: 260 }}>
            <h3 className="h6">Selected role</h3>
            {selectedRole ? (
              <>
                <p className="small mb-2">{selectedRole.description}</p>
                <p className="small mb-2">
                  Users with this role:{' '}
                  <Button variant="link" size="sm" className="p-0 align-baseline" onClick={() => navigate(companyUrl(`users?role=${selectedRole.id}`))}>
                    {selectedRole.userCount}
                  </Button>
                </p>
                <Button size="sm" variant="outline-secondary" onClick={() => toast.success('Role cloned (mock)')}>
                  Clone role
                </Button>
              </>
            ) : (
              <p className="text-muted small">Select a column.</p>
            )}
          </div>
        </div>
      )}

      <DrawerPanel isOpen={drawerOpen} onClose={() => setDrawerOpen(false)} title="New custom role" width={420}>
        <Form.Group className="mb-2">
          <Form.Label>Role name</Form.Label>
          <Form.Control value={newRole.name} onChange={(e) => setNewRole((s) => ({ ...s, name: e.target.value }))} />
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label>Description</Form.Label>
          <Form.Control as="textarea" rows={2} value={newRole.description} onChange={(e) => setNewRole((s) => ({ ...s, description: e.target.value }))} />
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label>Base on existing role</Form.Label>
          <Form.Select value={newRole.baseRoleId} onChange={(e) => setNewRole((s) => ({ ...s, baseRoleId: e.target.value }))}>
            {['tenant_admin', 'scheduler', 'auditor', 'noc_operator', 'viewer'].map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <p className="small text-muted">A full permission grid is copied from the base role; adjust cells after creation.</p>
        <Button onClick={createRole}>Create</Button>
      </DrawerPanel>
    </div>
  );
}
